import React from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Box, Paper, Tabs, Tab, Typography } from '@mui/material';

// Restaurant Orders workspace — WORKFLOW CONSOLIDATION ONLY.
//
// A horizontal tab strip over the existing ordering pages, so staff run the
// whole order lifecycle (order -> KOT -> bill -> payment -> history) without
// leaving one workspace. Every tab navigates to an EXISTING route; pages,
// billing, KOT, printing and permissions are untouched.
//
// Dine In / Takeaway / Delivery share the POS page (/admin/CreateOrder) and
// differ only by the ?type= query, which the POS reads to preset its existing
// order-type selector.
const TABS = [
  {
    label: 'Dine In',
    path: '/admin/CreateOrder',
    type: 'DineIn',
    menuKey: 'CreateOrder',
  },
  { label: 'Room Service', path: '/admin/Order', menuKey: 'Order' },
  {
    label: 'Takeaway',
    path: '/admin/CreateOrder',
    type: 'Takeaway',
    menuKey: 'CreateOrder',
  },
  {
    label: 'Delivery',
    path: '/admin/CreateOrder',
    type: 'Delivery',
    menuKey: 'CreateOrder',
  },
  { label: 'Quick Bill', path: '/admin/QuickBill', menuKey: 'CreateOrder' },
  { label: 'Tables', path: '/admin/TableOrders', menuKey: 'RestaurantOrder' },
  {
    label: 'Payments',
    path: '/admin/RestaurantPayments',
    menuKey: 'RestaurantOrder',
  },
  {
    label: 'Order History',
    path: '/admin/OrderHistory',
    menuKey: 'RestaurantInvoiceReport',
  },
];

// Same visibility rule as the sidebar: master admin sees all, otherwise the
// stored permission grants decide; fail-open when no permission list exists.
const allowedTabs = () => {
  let user = null;
  try {
    user = JSON.parse(sessionStorage.getItem('user'));
  } catch (_) {}
  if (user?.isMaster) return TABS;

  const raw = sessionStorage.getItem('permissions');
  if (!raw) return TABS;
  try {
    const keys = new Set(JSON.parse(raw).map((p) => p.menuKey));
    return TABS.filter((t) => keys.has(t.menuKey));
  } catch (_) {
    return TABS;
  }
};

export default function RestaurantOrdersLayout() {
  const location = useLocation();
  const navigate = useNavigate();

  const tabs = React.useMemo(allowedTabs, []);
  const current = location.pathname.replace(/\/+$/, '');
  const typeParam =
    new URLSearchParams(location.search).get('type') || 'DineIn';

  const activeIdx = tabs.findIndex((t) => {
    if (current !== t.path && !current.startsWith(t.path + '/')) return false;
    // The POS route hosts three tabs — disambiguate by ?type=.
    if (t.path === '/admin/CreateOrder') return t.type === typeParam;
    return true;
  });

  const go = (t) =>
    navigate(t.type ? `${t.path}?type=${t.type}` : t.path);

  return (
    <Box sx={{ width: '100%' }}>
      <Paper
        elevation={0}
        square
        sx={{
          borderBottom: '1px solid',
          borderColor: 'divider',
          px: { xs: 1, sm: 2 },
          pt: 1,
          bgcolor: 'background.paper',
        }}>
        <Typography
          variant='subtitle2'
          sx={{ fontWeight: 700, color: 'text.secondary', mb: 0.5 }}>
          Restaurant Orders
        </Typography>
        <Tabs
          value={activeIdx === -1 ? false : activeIdx}
          onChange={(_, idx) => go(tabs[idx])}
          variant='scrollable'
          scrollButtons='auto'
          allowScrollButtonsMobile
          sx={{
            minHeight: 40,
            '& .MuiTab-root': {
              minHeight: 40,
              textTransform: 'none',
              fontWeight: 600,
            },
          }}>
          {tabs.map((t) => (
            <Tab key={t.label} label={t.label} />
          ))}
        </Tabs>
      </Paper>

      {/* The existing page renders below, completely unchanged. */}
      <Outlet />
    </Box>
  );
}